import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../useAuth';
import SEOHelmet from '../components/SEOHelmet';
import ComplianceCard from '../components/ComplianceCard';
import { CyclePrintView } from '../components/CyclePrintView';
import { IComplianceResult } from '../types';

// ── Demo data ────────────────────────────────────────────────────────────────
// daysRemaining computed live so the badge is always accurate
const daysUntil = (dateStr: string) =>
  Math.max(0, Math.round((new Date(dateStr).getTime() - Date.now()) / 86_400_000));

// Attorney NY: biennial cycle running May 1, 2025 – Apr 30, 2027 (based on bar admission month)
const exampleInProgress: IComplianceResult = {
  cycleId: 'demo-1', cycleName: '2025–2027 Renewal Cycle',
  professionName: 'Attorney', professionID: 'LAW', stateName: 'New York', stateID: 32,
  totalEarned: 12, totalRequired: 24, percentComplete: 50, isMet: false,
  categories: [
    { categoryTag: 'GENERAL', categoryName: 'General CLE', required: 20, earned: 10, isMet: false },
    { categoryTag: 'ETHICS',  categoryName: 'Ethics',      required: 4,  earned: 2,  isMet: false },
  ],
  warnings: ['12 more credits needed'], daysRemaining: daysUntil('2027-04-30'),
  cycleStartDate: '2025-05-01', cycleEndDate: '2027-04-30',
};

// Architect CA: biennial cycle running Sep 1, 2024 – Aug 31, 2026 (based on license issue date)
const exampleNearlyComplete: IComplianceResult = {
  cycleId: 'demo-2', cycleName: '2024–2026 Renewal Cycle',
  professionName: 'Architect', professionID: 'ARCH', stateName: 'California', stateID: 5,
  totalEarned: 22, totalRequired: 24, percentComplete: 92, isMet: false,
  categories: [
    { categoryTag: 'GENERAL', categoryName: 'General',       required: 18, earned: 18, isMet: true  },
    { categoryTag: 'HSW',     categoryName: 'Health/Safety', required: 5,  earned: 4,  isMet: false },
  ],
  warnings: ['2 more credits needed'], daysRemaining: daysUntil('2026-08-31'),
  cycleStartDate: '2024-09-01', cycleEndDate: '2026-08-31',
};

// CPA NY: annual cycle Jan 1 – Dec 31, 2026
const exampleCompliant: IComplianceResult = {
  cycleId: 'demo-3', cycleName: '2026 Annual CPE Cycle',
  professionName: 'CPA', professionID: 'CPA', stateName: 'New York', stateID: 32,
  totalEarned: 24, totalRequired: 40, percentComplete: 60, isMet: false,
  categories: [
    { categoryTag: 'GENERAL', categoryName: 'General CPE', required: 28, earned: 16, isMet: false },
    { categoryTag: 'TAX',     categoryName: 'Taxation',    required: 8,  earned: 4,  isMet: false },
    { categoryTag: 'ETHICS',  categoryName: 'Ethics',      required: 4,  earned: 4,  isMet: true  },
  ],
  warnings: ['16 more credits needed'], daysRemaining: daysUntil('2026-12-31'),
  cycleStartDate: '2026-01-01', cycleEndDate: '2026-12-31',
};

const mockPrintClasses = [
  { _id: '1', classname: 'Ethics for CPAs in Public Practice',  classdate: '2026-02-15', classLU: 4.0, took: true },
  { _id: '2', classname: 'Federal Tax Law Changes 2026',        classdate: '2026-04-10', classLU: 4.0, took: true },
  { _id: '3', classname: 'Financial Statement Analysis',        classdate: '2026-05-20', classLU: 8.0, took: true },
  { _id: '4', classname: 'Audit Standards & Procedures',        classdate: '2026-06-05', classLU: 8.0, took: true },
];
const mockCategoryCompliance = [
  { categoryTag: 'GENERAL', description: 'General Accounting & Business', minCredits: 28, earned: 16, planned: 0 },
  { categoryTag: 'TAX',     description: 'Taxation',                       minCredits: 8,  earned: 4,  planned: 0 },
  { categoryTag: 'ETHICS',  description: 'Ethics (NY Mandatory)',          minCredits: 4,  earned: 4,  planned: 0 },
];

// ── Shared style helpers ──────────────────────────────────────────────────────
const sectionPad = '7rem 0';

const featureCards = [
  { icon: 'bi-map',              title: 'Multi-State Tracking',    body: 'Manage licenses and CE requirements across every state from a single dashboard. All renewal cycles at a glance.' },
  { icon: 'bi-folder2-open',     title: 'Certificate Storage',     body: 'Store all course certificates in the cloud. Access them anytime and never lose critical documentation.' },
  { icon: 'bi-bell',             title: 'Smart Reminders',         body: 'Receive timely notifications about upcoming deadlines and missing credits before it is ever an issue.' },
  { icon: 'bi-graph-up-arrow',   title: 'Compliance Dashboard',    body: 'Real-time compliance status for every license. Visual progress tracking shows exactly where you stand.' },
  { icon: 'bi-shield-lock',      title: 'Secure & Private',        body: 'Your certificates and personal information are encrypted and always safe. We never share your data.' },
  { icon: 'bi-lightning-charge', title: 'Instant Calculations',    body: 'Upload a certificate and get credit calculations immediately. No manual entry, no waiting.' },
];

const professions = [
  'Attorneys', 'CPAs', 'Architects', 'Professional Engineers',
  'Mental Health Counselors', 'Social Workers', 'Physical Therapists', 'Landscape Architects',
  'Occupational Therapists', 'Psychologists', 'Speech-Language Pathologists', 'Interior Designers',
  'Dental Hygienists', 'Appraisers', 'Marriage & Family Therapists', 'Clinical Social Workers',
];

const steps = [
  { n: '01', title: 'Add Your Licenses',    body: 'Enter your professional licenses and state requirements. We support 30+ professions across all 50 states.' },
  { n: '02', title: 'Upload Certificates',  body: 'Add completed CE classes with certificates. Drag and drop files or photograph with your phone.' },
  { n: '03', title: 'Track Progress',       body: 'Monitor compliance status in real-time. See exactly what you need for each renewal cycle.' },
  { n: '04', title: 'Stay Compliant',       body: 'Receive timely reminders and never miss a deadline. Renew with confidence every time.' },
];

// ── Profession rail data ──────────────────────────────────────────────────────
interface ProfEntry {
  name: string;
  abbr: string;
  slug: string;
  color: string;
  meta: string;
}

const FEATURED_PROFESSIONS: ProfEntry[] = [
  { name: 'Architect',                       abbr: 'ARCH', slug: 'architect',                        color: 'var(--color-prof-arch)',  meta: 'AIA · NCARB'    },
  { name: 'Professional Engineer',           abbr: 'PE',   slug: 'professional-engineer',            color: 'var(--color-prof-pe)',    meta: 'NCEES · PE'     },
  { name: 'CPA',                             abbr: 'CPA',  slug: 'certified-public-accountant',      color: 'var(--color-prof-cpa)',   meta: 'NASBA · AICPA'  },
  { name: 'Dentist',                         abbr: 'DDS',  slug: 'dentist',                          color: 'var(--color-prof-dent)',  meta: 'ADA · State'    },
  { name: 'Attorney',                        abbr: 'LAW',  slug: 'attorney',                         color: 'var(--color-prof-law)',   meta: 'ABA · State Bar' },
  { name: 'Landscape Architect',             abbr: 'LA',   slug: 'landscape-architect',              color: 'var(--color-prof-la)',    meta: 'ASLA · CLARB'   },
  { name: 'Lic. Clinical Social Worker',     abbr: 'LCSW', slug: 'licensed-clinical-social-worker',  color: 'var(--color-prof-sw)',    meta: 'NASW · ASWB'    },
  { name: 'Occupational Therapist',          abbr: 'OT',   slug: 'occupational-therapist',           color: 'var(--color-text-muted)', meta: 'AOTA · State'   },
];

const OTHER_PROFESSIONS: ProfEntry[] = [
  { name: 'Social Worker',                         abbr: 'SW',   slug: 'social-worker',                    color: 'var(--color-prof-sw)',   meta: 'NASW · State'   },
  { name: 'Psychologist',                          abbr: 'PSYC', slug: 'psychologist',                     color: 'var(--color-prof-lmhc)', meta: 'APA · State'    },
  { name: 'Physical Therapist',                    abbr: 'PT',   slug: 'physical-therapist',               color: 'var(--color-text-muted)',meta: 'APTA · State'   },
  { name: 'Licensed Mental Health Counselor',      abbr: 'LMHC', slug: 'licensed-mental-health-counselor', color: 'var(--color-prof-lmhc)', meta: 'AMHCA · NYSED'  },
  { name: 'Marriage & Family Therapist',           abbr: 'LMFT', slug: 'marriage-family-therapist',        color: 'var(--color-prof-lmhc)', meta: 'AAMFT · State'  },
  { name: 'Speech-Language Pathologist',           abbr: 'SLP',  slug: 'speech-language-pathologist',      color: 'var(--color-text-muted)',meta: 'ASHA · State'   },
  { name: 'Optometrist',                           abbr: 'OD',   slug: 'optometrist',                      color: 'var(--color-text-muted)',meta: 'AOA · State'    },
];

// ── Component ─────────────────────────────────────────────────────────────────
const Landing: React.FC = () => {
  const { user } = useAuth();

  return (
    <>
      <SEOHelmet
        title="Home - Professional Continuing Education Credits Tracker"
        description="Track your professional continuing education credits effortlessly. CEC Track helps architects, engineers, pharmacists, landscape architects, and other professionals manage renewal cycles and stay compliant."
        keywords="continuing education credits, CEC tracking, professional license renewal, architect CECs, engineer PDH, pharmacist CE, landscape architect credits, license compliance"
        canonical="https://cectrack.com/"
        structuredData={[
          {
            "@context": "https://schema.org", "@type": "Organization",
            "name": "CEC Track", "url": "https://cectrack.com",
            "logo": "https://cectrack.com/logo-512.png", "foundingDate": "2024",
            "contactPoint": { "@type": "ContactPoint", "email": "support@cectrack.com", "contactType": "customer support" },
          },
          { "@context": "https://schema.org", "@type": "WebSite", "name": "CEC Track", "url": "https://cectrack.com" },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CEC Track",
            "applicationCategory": "BusinessApplication",
            "applicationSubCategory": "Compliance Tracking Software",
            "operatingSystem": "Web Browser",
            "url": "https://cectrack.com",
            "description": "Continuing education compliance tracking software for licensed professionals in the United States. Track CE credits by mandatory category, manage multi-state renewal cycles, and receive deadline reminders. Supports architects, engineers, CPAs, attorneys, nurses, pharmacists, social workers, and 20+ other licensed professions.",
            "featureList": [
              "Mandatory CE category tracking (Ethics, HSW, Laws & Rules)",
              "Multi-state license management on one dashboard",
              "Renewal deadline email reminders",
              "CE certificate storage for audit readiness",
              "PDF compliance reports",
              "Team and firm compliance dashboards",
              "20+ professions supported",
            ],
            "audience": {
              "@type": "Audience",
              "audienceType": "Licensed professionals: architects, engineers, CPAs, attorneys, nurses, pharmacists, social workers, mental health counselors, dentists, landscape architects",
            },
            "offers": [
              { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD", "description": "1 renewal cycle, unlimited classes, no credit card required" },
              { "@type": "Offer", "name": "Pro", "price": "9", "priceCurrency": "USD", "description": "Unlimited cycles, multi-state tracking, PDF reports, deadline alerts" },
            ],
            "screenshot": "https://cectrack.com/og-image.png",
            "inLanguage": "en-US",
            "isAccessibleForFree": true,
          },
        ]}
      />

      {/* ══════════════════════════════════════════════════════════════════════
          HERO — editorial 2-column, paper background
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        background: 'var(--color-surface-paper)',
        borderBottom: '1px solid var(--color-border)',
        padding: '5rem 0 4rem',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Dot-grid texture */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(15,31,61,0.05) 1px, transparent 0)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
        }} />

        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 2rem', position: 'relative' }}>
          <div className="row g-5 align-items-start">

            {/* ── LEFT: Editorial hero copy ── */}
            <div className="col-lg-7">
              {/* Eyebrow */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--color-accent)', flexShrink: 0, display: 'inline-block',
                }} />
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
                  fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                }}>
                  Professional License Management
                </span>
                <span style={{
                  marginLeft: 'auto', fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', opacity: 0.55,
                }}>
                  Vol. {new Date().getFullYear()}
                </span>
              </div>

              {/* Trial promo badge */}
              {!user && (
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.65rem 1.25rem', marginBottom: '1.75rem',
                  background: '#f59e0b',
                  borderRadius: 999,
                  boxShadow: '0 4px 14px rgba(245,158,11,0.4)',
                }}>
                  <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>🎉</span>
                  <span style={{
                    fontFamily: 'var(--font-body)', fontSize: '1rem',
                    fontWeight: 700, color: '#0F1F3D',
                  }}>
                    60-day Pro trial for every new account — no credit card required
                  </span>
                </div>
              )}

              {/* Giant headline */}
              <h1 style={{
                fontFamily: 'var(--font-display)', fontWeight: 700,
                fontSize: 'clamp(4rem, 7.5vw, 9rem)',
                lineHeight: 0.95, letterSpacing: '-0.03em',
                color: 'var(--color-primary)', marginBottom: '2rem',
              }}>
                Never<br />
                Miss a<br />
                <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>Renewal.</em>
              </h1>

              {/* Lede */}
              <p style={{
                fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
                lineHeight: 1.75, color: 'var(--color-text-muted)',
                marginBottom: '2.5rem', maxWidth: '500px',
              }}>
                Track continuing education credits across every state in one platform.
                Stay compliant, organized, and ready for renewal — always.
              </p>

              {/* CTA row */}
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '3rem' }}>
                <Link
                  to={user ? '/dashboard' : '/register'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.875rem 2rem',
                    background: 'var(--color-primary)', color: '#ffffff',
                    fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)',
                    fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)',
                    textDecoration: 'none',
                    transition: 'background var(--transition-base), transform var(--transition-base)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-primary-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {user ? 'Go to Dashboard' : 'Get Started Free'}
                  <i className="bi bi-arrow-right" />
                </Link>
                <Link
                  to="/how-it-works"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.875rem 2rem',
                    background: 'transparent', color: 'var(--color-text)',
                    fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-medium)',
                    fontSize: 'var(--fs-base)', borderRadius: 'var(--radius-md)',
                    textDecoration: 'none', border: '1px solid var(--color-border-strong)',
                    transition: 'border-color var(--transition-base)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-text)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-strong)'; }}
                >
                  How It Works
                </Link>
                <span style={{
                  fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)',
                  color: 'var(--color-text-muted)',
                }}>
                  {user ? 'Free forever · No credit card' : '60-day Pro trial · No credit card'}
                </span>
              </div>

              {/* Stats strip */}
              <div style={{
                display: 'flex', gap: '2.5rem', flexWrap: 'wrap',
                paddingTop: '2rem', borderTop: '1px solid var(--color-border)',
              }}>
                {[
                  { n: '50',   unit: 'states',        sub: 'all covered'    },
                  { n: '30+',  unit: 'professions',    sub: 'and counting'   },
                  { n: '99%',  unit: 'on-time',        sub: 'renewal rate'   },
                  { n: '1.2k', unit: 'professionals',  sub: 'trust us'       },
                ].map(({ n, unit, sub }) => (
                  <div key={unit} style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                    <span style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: 'var(--fs-2xl)', color: 'var(--color-primary)', lineHeight: 1,
                    }}>{n}</span>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
                      color: 'var(--color-accent)', fontWeight: 'var(--fw-semibold)',
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>{unit}</span>
                    <span style={{
                      fontFamily: 'var(--font-body)', fontSize: '0.7rem',
                      color: 'var(--color-text-muted)',
                    }}>{sub}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT: Profession rail ── */}
            <div className="col-lg-5 d-none d-lg-block">
              <div style={{ position: 'relative' }}>
                {/* Corner ribbon — 60-day Pro trial */}
                {!user && (
                  <div style={{
                    position: 'absolute', top: -78, right: -78, width: 170, height: 170,
                    overflow: 'hidden', zIndex: 3, pointerEvents: 'none',
                  }}>
                    <div style={{
                      position: 'absolute', top: 32, right: -44, width: 220,
                      transform: 'rotate(45deg)',
                      background: 'linear-gradient(160deg, var(--color-primary) 0%, #0A1628 100%)',
                      color: '#fff', textAlign: 'center', padding: '0.55rem 0 0.65rem',
                      boxShadow: '0 6px 14px rgba(10,22,40,0.3)',
                    }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: '1.2rem', letterSpacing: '0.01em', lineHeight: 1 }}>
                        60 DAYS
                      </div>
                      <div style={{ fontFamily: 'var(--font-body)', fontWeight: 600, fontSize: '0.62rem', letterSpacing: '0.09em', textTransform: 'uppercase', color: '#f59e0b', marginTop: '0.2rem' }}>
                        Pro Trial
                      </div>
                    </div>
                  </div>
                )}
              <div style={{
                background: 'var(--color-surface-raised)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-md)',
                overflow: 'hidden',
                position: 'sticky',
                top: 'calc(var(--navbar-height) + 1.5rem)',
              }}>
                {/* Rail header */}
                <div style={{
                  padding: '1.25rem 1.5rem 1rem',
                  borderBottom: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)',
                    fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>
                    Select your profession
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
                    color: 'var(--color-accent)', background: 'var(--color-accent-light)',
                    padding: '0.2rem 0.5rem', borderRadius: 'var(--radius-sm)',
                  }}>
                    30+ professions
                  </span>
                </div>

                {/* Profession list — flat, no section labels */}
                <div style={{ padding: '0.75rem 1.5rem 0.5rem' }}>
                  {[...FEATURED_PROFESSIONS, ...OTHER_PROFESSIONS].map(p => (
                    <ProfRow key={p.abbr} prof={p} />
                  ))}
                  {/* "More coming" indicator */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    padding: '0.4rem 0.25rem',
                    opacity: 0.45,
                  }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--color-text-muted)', letterSpacing: '0.1em' }}>
                      · · ·
                    </span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                      and more professions
                    </span>
                  </div>
                </div>

                {/* Rail footer */}
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid var(--color-border)',
                  background: 'var(--color-surface-muted)',
                  textAlign: 'center',
                }}>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)' }}>
                    All 50 states ·{' '}
                    <Link to="/how-it-works" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontWeight: 'var(--fw-medium)' }}>
                      How it works →
                    </Link>
                  </span>
                </div>
              </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 1 — Live compliance cards demo
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="01" color="var(--color-accent)"
            title="Track All Your Licenses in One Dashboard"
            sub="See compliance status across every state and profession at a glance."
          />
          <div className="row g-4" style={{ marginBottom: '2rem' }}>
            <div className="col-lg-4"><ComplianceCard compliance={exampleInProgress}      showCycleLink={false} /></div>
            <div className="col-lg-4"><ComplianceCard compliance={exampleNearlyComplete}  showCycleLink={false} /></div>
            <div className="col-lg-4"><ComplianceCard compliance={exampleCompliant}       showCycleLink={false} /></div>
          </div>
          <Callout icon="bi-info-circle" color="var(--color-accent)">
            <strong>See your progress at a glance:</strong> Track credits earned, view category breakdowns, monitor deadlines, and know exactly what you need for each license.
          </Callout>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 2 — Quick 3-step setup
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="02" color="var(--color-success)"
            title="Quick 3-Step Setup"
            sub="Add your license info, classes, and assign them to cycles."
          />
          <div className="row align-items-center g-3" style={{ marginBottom: '2rem' }}>
            {[
              { icon: 'bi-patch-check', title: 'Add License Info', items: ['State & Profession', 'Cycle Start/End Dates', 'Total Credits Required'], accent: 'var(--color-primary)' },
              { icon: 'bi-journal-bookmark', title: 'Add Your Classes', items: ['Course Name', 'Learning Units (LU)', 'Completion Date'], accent: 'var(--color-success)' },
              { icon: 'bi-link-45deg', title: 'Assign to Cycles', items: ['Match Classes to Cycles', 'Auto Credit Calculation', 'Category Assignment'], accent: 'var(--color-accent)' },
            ].map((box, i) => (
              <React.Fragment key={box.title}>
                <div className="col-lg-3">
                  <div style={{
                    padding: '2rem 1.5rem',
                    borderRadius: 'var(--radius-md)',
                    border: `2px solid ${box.accent}22`,
                    background: 'var(--color-surface-raised)',
                    boxShadow: 'var(--shadow-card)',
                    minHeight: '220px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center',
                  }}>
                    <i className={`bi ${box.icon}`} style={{ fontSize: '2.5rem', color: box.accent, marginBottom: '1rem', display: 'block' }} />
                    <h5 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', marginBottom: '1rem' }}>{box.title}</h5>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                      {box.items.map(item => (
                        <li key={item} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', marginBottom: '0.4rem' }}>
                          <span style={{ color: box.accent, marginRight: '0.4rem' }}>✓</span>{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {i < 2 && (
                  <div className="col-lg-1 text-center d-none d-lg-block">
                    <i className="bi bi-arrow-right" style={{ fontSize: '1.5rem', color: 'var(--color-text-muted)' }} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <Callout icon="bi-check-circle" color="var(--color-success)">
            <strong>Simple and intuitive:</strong> Set up in minutes. The system automatically tracks progress and calculates credits for every category.
          </Callout>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          STEP 3 — PDF report demo
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <StepHeader n="03" color="var(--color-accent-warm)"
            title="Generate Detailed Compliance Reports"
            sub="Comprehensive reports ready for license renewal submissions, anytime."
          />
          <div style={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-lg)',
            marginBottom: '2rem',
          }}>
            <CyclePrintView
              cycleName="2026 CPA Annual CPE"
              state="NY" period="January 1, 2026 – December 31, 2026"
              creditsRequired={40} totalLUCompleted={24} totalLUPlanned={0} luToBeCompleted={16}
              attachedClasses={mockPrintClasses}
              firstName="John" lastName="Doe"
              professionName="Certified Public Accountant" licenseNumber="CPA-12345678"
              creditTerm="CPE Hours" categoryCompliance={mockCategoryCompliance}
            />
          </div>
          <Callout icon="bi-file-earmark-text" color="var(--color-accent-warm)">
            <strong>Professional reports ready to print:</strong> Generate comprehensive compliance reports for renewals, audits, or your own records. Certificates are securely stored and linked to each course.
          </Callout>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FEATURES grid
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#ffffff', marginBottom: '0.75rem', lineHeight: 1.1 }}>
              Everything You Need to Stay Compliant
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.6)', maxWidth: '560px', margin: '0 auto' }}>
              Professional-grade tools built for licensed professionals managing multi-state requirements.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.25rem' }}>
            {featureCards.map(({ icon, title, body }) => (
              <div
                key={title}
                style={{
                  padding: '2rem',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 'var(--radius-md)',
                  transition: 'background var(--transition-base), border-color var(--transition-base), transform var(--transition-base)',
                  cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(42,191,191,0.08)'; e.currentTarget.style.borderColor = 'rgba(42,191,191,0.3)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ marginBottom: '1.25rem' }}>
                  <i className={`bi ${icon}`} style={{ fontSize: '1.75rem', color: 'var(--color-accent)' }} />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-lg)', color: '#ffffff', marginBottom: '0.75rem' }}>{title}</h3>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          HOW IT WORKS — numbered steps
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-surface-muted)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeader title="Simple Process, Powerful Results" sub="Get started in minutes with our streamlined workflow." />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '3rem' }}>
            {steps.map(({ n, title, body }) => (
              <div key={n} style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: '64px', height: '64px', borderRadius: '50%',
                  background: 'var(--color-primary)',
                  marginBottom: '1.5rem',
                }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--color-accent)' }}>{n}</span>
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 'var(--fs-lg)', color: 'var(--color-text)', marginBottom: '0.75rem' }}>{title}</h3>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', lineHeight: 1.7, margin: 0 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PROFESSIONS tag cloud
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{ padding: sectionPad, background: 'var(--color-surface)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1.5rem' }}>
          <SectionHeader title="Built for Licensed Professionals" sub="Supporting 30+ professions across all 50 states." />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.625rem', justifyContent: 'center', maxWidth: '860px', margin: '0 auto 2rem' }}>
            {professions.map(p => (
              <span
                key={p}
                style={{
                  padding: '0.5rem 1.1rem',
                  background: 'var(--color-surface-raised)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-full)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 'var(--fw-medium)',
                  color: 'var(--color-text-muted)',
                  transition: 'background var(--transition-fast), border-color var(--transition-fast), color var(--transition-fast)',
                  cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent-light)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; e.currentTarget.style.color = 'var(--color-accent-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-surface-raised)'; e.currentTarget.style.borderColor = 'var(--color-border)'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
              >
                {p}
              </span>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text-muted)', margin: 0 }}>
            …and many more licensed professionals requiring continuing education credits.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CTA — dark navy, Cormorant large italic
      ══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: '7rem 0',
        background: 'linear-gradient(160deg, #0a1628 0%, var(--color-primary) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', bottom: '-15%', left: '-5%',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(42,191,191,0.06) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div className="container" style={{ maxWidth: '760px', margin: '0 auto', padding: '0 1.5rem', textAlign: 'center', position: 'relative' }}>
          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 'clamp(2.25rem, 5vw, 4rem)',
            lineHeight: 1.1,
            color: '#ffffff',
            marginBottom: '1.25rem',
            letterSpacing: '-0.01em',
          }}>
            Ready to Simplify Your<br />
            <em style={{ color: 'var(--color-accent)', fontStyle: 'italic' }}>License Renewals?</em>
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'rgba(255,255,255,0.6)', marginBottom: '2.5rem', lineHeight: 1.7 }}>
            Join hundreds of licensed professionals who trust CEC Track to manage their continuing education.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to={user ? '/dashboard' : '/register'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.875rem 2rem',
                background: 'var(--color-accent)', color: 'var(--color-primary)',
                fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-base)',
                borderRadius: 'var(--radius-md)', textDecoration: 'none',
                transition: 'background var(--transition-base), transform var(--transition-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-accent-hover)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              {user ? 'Go to Dashboard' : 'Get Started Free'}
              <i className="bi bi-arrow-right" />
            </Link>
            <Link
              to="/how-it-works"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.875rem 2rem',
                background: 'transparent', color: 'rgba(255,255,255,0.8)',
                fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-base)',
                borderRadius: 'var(--radius-md)', textDecoration: 'none',
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'border-color var(--transition-base), color var(--transition-base)',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            >
              Learn More
            </Link>
          </div>
          {!user && (
            <p style={{ marginTop: '1.5rem', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: '#f59e0b', fontWeight: 'var(--fw-semibold)' }}>
              🎉 60-day Pro trial included — no credit card required
            </p>
          )}
          <p style={{ marginTop: '0.75rem', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'rgba(255,255,255,0.4)' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: 'var(--color-accent)', textDecoration: 'none', fontWeight: 'var(--fw-medium)' }}>Sign in →</Link>
          </p>
        </div>
      </section>
    </>
  );
};

// ── Small shared sub-components ───────────────────────────────────────────────

// Large side-by-side step header: circle left, label + h2 right
const StepHeader = ({ n, color, title, sub }: { n: string; color: string; title: string; sub: string }) => (
  <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '3rem' }}>
    {/* Circle badge */}
    <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
      <div style={{
        width: '100px', height: '100px', borderRadius: '50%',
        background: color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 6px 24px ${color}55`,
      }}>
        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '2.75rem', color: '#fff', lineHeight: 1 }}>{n}</span>
      </div>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)',
        color, textTransform: 'uppercase', letterSpacing: '0.14em', whiteSpace: 'nowrap',
      }}>
        Step {n}
      </span>
    </div>
    {/* Heading — top-aligns with circle so the circle visually covers the heading area */}
    <div style={{ paddingTop: '0.25rem' }}>
      <h2 style={{
        fontFamily: 'var(--font-display)', fontWeight: 700,
        fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', color: 'var(--color-text)',
        marginBottom: '0.75rem', lineHeight: 1.15, letterSpacing: '-0.01em',
      }}>
        {title}
      </h2>
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)',
        color: 'var(--color-text-muted)', maxWidth: '600px', margin: 0, lineHeight: 1.6,
      }}>
        {sub}
      </p>
    </div>
  </div>
);

const SectionHeader = ({ title, sub }: { title: string; sub: string }) => (
  <div style={{ marginBottom: '3rem' }}>
    <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', color: 'var(--color-text)', marginBottom: '0.75rem', lineHeight: 1.15, letterSpacing: '-0.01em' }}>
      {title}
    </h2>
    <p style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-md)', color: 'var(--color-text-muted)', maxWidth: '600px', margin: 0, lineHeight: 1.6 }}>
      {sub}
    </p>
  </div>
);

const Callout = ({ icon, color, children }: { icon: string; color: string; children: React.ReactNode }) => (
  <div style={{
    padding: '1rem 1.25rem',
    background: `color-mix(in srgb, ${color} 8%, var(--color-surface-raised))`,
    border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
    borderLeft: `4px solid ${color}`,
    borderRadius: 'var(--radius-md)',
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
  }}>
    <i className={`bi ${icon}`} style={{ color, fontSize: '1.1rem', marginTop: '0.1rem', flexShrink: 0 }} />
    <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)', color: 'var(--color-text)', lineHeight: 1.65 }}>
      {children}
    </span>
  </div>
);

const ProfRow = ({ prof, muted = false }: { prof: ProfEntry; muted?: boolean }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <Link
      to={`/profession/${prof.slug}`}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.5rem 0.25rem',
        borderRadius: 'var(--radius-sm)',
        textDecoration: 'none',
        background: hovered ? 'var(--color-surface-muted)' : 'transparent',
        transition: 'background var(--transition-fast)',
        opacity: muted ? 0.65 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Color swatch */}
      <span style={{
        width: '10px', height: '10px', borderRadius: '50%',
        background: prof.color, flexShrink: 0,
      }} />
      {/* Name */}
      <span style={{
        fontFamily: 'var(--font-display)', fontSize: '1.1rem',
        fontWeight: 600, color: 'var(--color-text)', flex: 1,
        lineHeight: 1.2,
      }}>
        {prof.name}
      </span>
      {/* Abbr chip */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: '0.65rem',
        fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-muted)',
        background: 'var(--color-surface-muted)',
        padding: '0.15rem 0.4rem', borderRadius: 'var(--radius-sm)',
        letterSpacing: '0.05em',
      }}>
        {prof.abbr}
      </span>
      {/* Arrow */}
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)',
        color: hovered ? 'var(--color-accent)' : 'var(--color-border)',
        transition: 'color var(--transition-fast)',
      }}>→</span>
    </Link>
  );
};

export default Landing;
